// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { mapAssetsSearchToPositionFields } from '@client/banks/utils/map-assets-search-to-position';
import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { AutocompleteInput } from '@shared/ui-kit/components/autocomplete';
import { Label } from '@shared/ui-kit/components/input';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import type { AssetsSearchResp } from '@shared/data-access/interfaces/api/ai/assets';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import catalogStyles from '../../bank-upload/styles/institution-catalog.module.css';

interface PositionAssetSearchFieldProps {
  isin: string;
  error?: boolean;
  onIsinChange: (value: string) => void;
  onAssetSelect: (
    fields: ReturnType<typeof mapAssetsSearchToPositionFields>,
  ) => void | Promise<void>;
}

export const PositionAssetSearchField: FC<PositionAssetSearchFieldProps> = ({
  isin,
  error = false,
  onIsinChange,
  onAssetSelect,
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState(isin);

  useEffect(() => {
    setSearchValue(isin);
  }, [isin]);

  const onSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchValue(value);
      onIsinChange(value);
    },
    [onIsinChange],
  );

  const onSearchSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, asset?: AssetsSearchResp) => {
      if (!asset) {
        return;
      }

      const fields = mapAssetsSearchToPositionFields(asset);
      setSearchValue(fields.isin);
      onIsinChange(fields.isin);
      onAssetSelect(fields);
      event.target.value = fields.isin;
    },
    [onAssetSelect, onIsinChange],
  );

  return (
    <div className={catalogStyles['field-stack']}>
      <Label htmlFor="bank-upload-position-isin" required error={error}>
        <Translation
          i18nKey="aggregator.create-bank.positions.asset-search"
          ns="aggregator"
        />
      </Label>
      <AutocompleteInput
        name="isin"
        value={searchValue}
        onChangeValue={onSearchChange}
        onSelectValue={onSearchSelect}
        error={error}
        placeholder={t(
          'aggregator.create-bank.positions.asset-search-placeholder',
          {
            ns: 'aggregator',
          },
        )}
        leftIcon={<AppIcon icon="Search" color="disabled" size="base" />}
        required={false}
      />
    </div>
  );
};

PositionAssetSearchField.displayName = 'PositionAssetSearchField';
